import { Brand } from '@principle-theorem/principle-core';
import {
  IBrand,
  ICustomReportDefinedColumn,
  ICustomReportFilterValue,
  IReportFilterContext,
  MeasureDataType,
  ReportBuilderDataSourceId,
} from '@principle-theorem/principle-core/interfaces';
import {
  IAccessorContext,
  IQueryScope,
  IReportingQuery,
  IScopedQuery,
} from '@principle-theorem/reporting/interfaces';
import {
  asReffable,
  DocumentReference,
  Firestore,
  isDocRef,
  TypeGuard,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import { ReportBuilderDataSourceInstance } from './data-sources/report-builder-data-source-instance';
import {
  REPORT_BUILDER_DATA_SOURCES,
  resolveDataSource,
} from './data-sources/report-builder-data-sources';
import { CustomReportFilter } from './models/custom-report-filters';
import { resolveBigQueryDataset } from './helpers';
import { resolveFactTable } from './models/fact-tables';
import { QueryOptionResolver } from './query-option-resolvers';
import { QueryScopeBuilder } from './query-scope-config/query-scope-builder';

export interface IReportingQueryCheckRequest {
  brandRef: DocumentReference<IBrand>;
  datasourceId: ReportBuilderDataSourceId;
}

export const isReportingQueryCheckRequest =
  TypeGuard.interface<IReportingQueryCheckRequest>({
    brandRef: isDocRef,
    datasourceId: TypeGuard.enumValue(ReportBuilderDataSourceId),
  });

export interface IReportingQueryCheckResponse {
  datasourceId: ReportBuilderDataSourceId;
  results: IDatasourceQueryCheck;
}

export interface IReportingQueryCheck {
  success: boolean;
  message?: string;
}

export interface IDatapointCheck {
  success: boolean;
  error?: string;
}

export interface IDatasourceQueryCheck {
  [columnId: string]: IDatapointCheck;
}

export class QueryCheckHelpers {
  static async buildQuery(
    datasource: ReportBuilderDataSourceInstance,
    datapoint: ICustomReportDefinedColumn,
    context: IReportFilterContext
  ): Promise<IReportingQuery> {
    const value = await this.getFilterValue(datapoint, context);
    const filter = CustomReportFilter.init({
      id: datapoint.id,
      values: [value],
    });

    filter.comparison = CustomReportFilter.getComparison(filter);

    const accessorContext: IAccessorContext = {
      timezone: context.brand.settings.timezone,
    };
    return datasource.buildQuery([datapoint], [], [filter], accessorContext);
  }

  static async getFilterValue(
    datapoint: ICustomReportDefinedColumn,
    context: IReportFilterContext
  ): Promise<ICustomReportFilterValue> {
    await Promise.resolve();
    if (datapoint.measure.metadata.optionResolver) {
      const options = await QueryOptionResolver.resolve(
        context,
        datapoint.measure.metadata.optionResolver
      );
      const option = first(options);
      if (option) {
        return { label: option.label, value: option.value };
      }
    }

    switch (datapoint.measure.metadata.dataType) {
      case MeasureDataType.String:
        return { label: 'test', value: 'test' };
      case MeasureDataType.Number:
        return { label: '123', value: 123 };
      case MeasureDataType.Boolean:
        return { label: 'Yes', value: true };
      case MeasureDataType.Date:
        return { label: '2025-04-27', value: '2025-04-27T13:59:59.999Z' };
      default:
        return { label: 'test', value: 'test' };
    }
  }

  static async buildQueryScope(
    brandRef: DocumentReference<IBrand>
  ): Promise<IQueryScope> {
    const orgRef = Brand.organisationRef(asReffable(brandRef));
    const practices = await Firestore.getDocs(
      Brand.practiceCol(asReffable(brandRef))
    );

    return {
      orgRef,
      scopeRequests: {
        brand: { brandRef },
        practice: { practiceRefs: practices.map((item) => item.ref) },
      },
    };
  }

  static async buildScopedQuery(
    brandRef: DocumentReference<IBrand>,
    datasourceId: ReportBuilderDataSourceId,
    column: ICustomReportDefinedColumn
  ): Promise<IScopedQuery | undefined> {
    const datasource = resolveDataSource(
      datasourceId,
      REPORT_BUILDER_DATA_SOURCES
    );
    if (!datasource) {
      // eslint-disable-next-line no-console
      console.error(`No datasource found for ${datasourceId}`);
      return;
    }

    const brand = await Firestore.getDoc(brandRef);
    const organisation = await Firestore.getDoc(Brand.organisationRef(brand));

    const query = await QueryCheckHelpers.buildQuery(datasource, column, {
      organisation,
      brand,
    });

    const factTable = resolveFactTable(query.table);
    if (!factTable) {
      // eslint-disable-next-line no-console
      console.error(`No fact table found for ${query.table}`);
      return;
    }

    const scope = await QueryCheckHelpers.buildQueryScope(brandRef);
    const builder = new QueryScopeBuilder(factTable);
    return builder.buildScopedQuery(query, {
      ...scope,
      bigqueryDataset: resolveBigQueryDataset(organisation),
    });
  }
}
